import { useState, useEffect, useRef } from "react";
import "./checkout.scss";
import { useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import CheckoutForm from "./CheckoutForm";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { createPaymentIntent } from "../../components/api/api";
import Button from "../../components/Button/Button";
//Thoughts on an etransfer checkout process
const Checkout = () => {
  const [stripePublishableKey, setStripePublishableKey] = useState("");
  const [stripePromise, setStripePromise] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [clientSecret, setClientSecret] = useState("");
  const cartItems = useSelector((state) => state.storeSlice.cartContents);
  // const appliedCoupon = useSelector((state) => state.storeSlice.appliedCoupons);
  const { state } = useLocation(); // Assuming navigation state is used
  const appliedCoupons = state?.appliedCoupons || [];
  const { data } = useSelector((state) => ({
    data: state.storeSlice.pickupLocations,
  }));
  const isNavigatingAway = useRef(false);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/config`)
      .then((res) => res.json())
      .then((config) => {
        setStripePublishableKey(config.publishableKey);
      })
      .catch((error) => {
        console.error("Error fetching publishable key:", error);
      });
  }, []);

  useEffect(() => {
    if (stripePublishableKey) {
      setStripePromise(loadStripe(stripePublishableKey));
    }
  }, [stripePublishableKey]);

  useEffect(() => {
    // Don't redirect if we're already navigating away (e.g., to thank-you page)
    // Check if we're still on the checkout page
    if (isNavigatingAway.current || !location.pathname.includes('/checkout')) {
      return;
    }

    const totals = getTotals();
    const totalInCents = Math.round(
      parseFloat(totals.total.toFixed(2)) * 100,
    );
    
    // Only redirect if cart is empty AND we're still on checkout page
    // This prevents redirecting after successful order completion
    if (totalInCents <= 0 && cartItems.length === 0 && location.pathname.includes('/checkout')) {
      navigate("/store/review-cart");
      return;
    }

    // Don't create payment intent if cart is empty (likely after order completion)
    if (cartItems.length === 0) {
      return;
    }

    // Validate totals before creating payment intent
    if (totals.total < 0 || totals.subtotal < 0) {
      console.error("Invalid totals detected:", totals);
      navigate("/store/review-cart");
      return;
    }

    createPaymentIntent(totalInCents).then((res) => {
      if (!res) {
        console.error("Failed to create payment intent");
        navigate("/store/review-cart");
        return;
      }
      setClientSecret(res);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems, appliedCoupons, location.pathname]);

  const getTotals = () => {
    let totals = { subtotal: 0, tax: 0, credit: 0, couponSavings: 0, total: 0 };

    // Calculate the subtotal
    cartItems.forEach((item) => {
      totals.subtotal += parseFloat(item.price) * item.numInCart;
    });

    // Calculate the total coupon savings
    appliedCoupons.forEach((coupon) => {
      totals.couponSavings += parseFloat(coupon.dollarsSaved);
    });

    // Calculate tax (14% on subtotal after coupons)
    let subtotalAfterCoupons = totals.subtotal - totals.couponSavings;
    totals.tax = subtotalAfterCoupons * 0.14; // 14% tax rate for Nova Scotia

    // Calculate the credit card fee (3% on subtotal after coupons + tax)
    totals.credit = (subtotalAfterCoupons + totals.tax) * 0.03;

    // Calculate the grand total for credit card
    totals.total = subtotalAfterCoupons + totals.tax + totals.credit;
    return totals;
  };

  const options = {
    clientSecret: clientSecret,
  };

  if (!clientSecret || !stripePromise)
    return (
      <div className="checkout-container">
        <div className="checkout-title-container">
          <h1>Let's Get You Checked Out</h1>
          <Button onClick={() => navigate("review-cart")}>
            Return to Cart
          </Button>
          <h2 className="loading">Loading...</h2>
        </div>
      </div>
    );
  return (
    <div className="checkout-container">
      <div className="checkout-title-container">
        <Button onClick={() => navigate("/store/review-cart")}>Return to Cart</Button>
        <h1>Let's Get You Checked Out</h1>
      </div>
      <div className="checkout-form-container">
        <Elements stripe={stripePromise} options={options}>
          <CheckoutForm
            getTotals={getTotals}
            pickupLocations={data}
            appliedCoupons={appliedCoupons}
            onOrderComplete={() => {
              isNavigatingAway.current = true;
            }}
          />
        </Elements>
      </div>
    </div>
  );
};

export default Checkout;
